import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { Grid, Input, Segment } from 'semantic-ui-react';

import _get from 'lodash/get';
import { useDispatch, useSelector } from 'react-redux';

import LocationSelect from 'components/pages/ecommerce/common/LocationSelect';

import { getFilteredReviews } from './ReviewsTable.utils';
import { updateReview as updateReviewConnect } from '../../../../../actions/business';
import { selectReviews } from '../../../../../selectors/reviews';
import useAsyncEffect from '../../../foundation/hooks/use-async-effect';
import useReviews from '../../../foundation/hooks/use-reviews/use-reviews';
import PaginatedTable from '../../../tables/components';
import ReviewsTableBody from '../ReviewsTableBody';
import ReviewsTableHeader from '../ReviewsTableHeader/ReviewsTableHeader';
import ReviewsTableModal from '../ReviewsTableModal';
import StatusSelector from '../StatusSelector/StatusSelector';

import './ReviewsTable.scss';

const propTypes = {
  sources: PropTypes.arrayOf(PropTypes.string),
};

const defaultProps = {
  sources: [],
};

export default function ReviewsTable({ sources }) {
  const dispatch = useDispatch();
  const reviews = useSelector(selectReviews);
  const { refreshReviews } = useReviews();

  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const updateReview = (payload) => dispatch(updateReviewConnect(payload));
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const filteredReviews = getFilteredReviews(reviews, {
    sources,
    status: selectedStatus,
    searchQuery: searchValue,
    locationId: selectedLocationId,
  });

  function handleInputChange(e, target, index) {
    updateReview({ field: target, fieldValue: e.target.value, index });
  }

  function handleReplyClick(review) {
    setSelectedReview(review);
    setReplyModalOpen(true);
  }

  async function closeReplyModal() {
    setReplyModalOpen(false);
    setSelectedReview(null);
    await refreshReviews();
  }

  function handleSearchChange(e, { value }) {
    setSearchValue(value);
  }

  function handleClearQuery() {
    setSearchValue('');
    setSelectedStatus('');
    setSelectedLocationId('');
  }

  function onChangeLocation(newLocation) {
    const newLocationId = _get(newLocation, 'id');
    setSelectedLocationId(newLocationId);
  }

  useAsyncEffect(async () => {
    try {
      setIsLoading(true);
      await refreshReviews();
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
    }
  }, []);

  return (
    <Segment vertical loading={isLoading}>
      <Grid>
        <Grid.Row>
          <Grid.Column width="8">
            <Input
              style={{ width: '100%' }}
              placeholder="Search reviews"
              icon="search"
              iconPosition="left"
              value={searchValue}
              onChange={(e, data) => handleSearchChange(e, data, setSearchValue)}
            />
          </Grid.Column>
          <Grid.Column width="4">
            <StatusSelector value={selectedStatus} onChange={setSelectedStatus} />
          </Grid.Column>
          <Grid.Column width="4">
            <LocationSelect
              activeLocation={selectedLocationId}
              onChangeLocation={onChangeLocation}
              global={false}
              fluid
              hideIfSingleLocation
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <PaginatedTable
        loading={isLoading}
        tableHeader={<ReviewsTableHeader />}
        tableBody={
          <ReviewsTableBody
            filteredReviews={filteredReviews}
            updateReview={updateReview}
            handleInputChange={handleInputChange}
            handleReplyClick={handleReplyClick}
            onClearQuery={handleClearQuery}
          />
        }
        onPageChange={() => {}}
        activePage={1}
        totalPages={1}
        columns={8}
      />

      {/* TODO: Add error message */}
      {/* {errorMessage && <Message negative>{errorMessage}</Message>} */}

      <ReviewsTableModal
        review={selectedReview}
        open={replyModalOpen}
        onClose={closeReplyModal}
        setReplyModalOpen={setReplyModalOpen}
      />
    </Segment>
  );
}

ReviewsTable.propTypes = propTypes;
ReviewsTable.defaultProps = defaultProps;
